import { ApiClient, ApiError } from "@hakili/api-client";
import { clientProfileWriteAnswerLenientSchema } from "@hakili/contracts";
import { empreinteAppareil } from "@hakili/domain-types";
import { createQueryClient, type QueryClient } from "@hakili/app-shell";
import type {
  BuyerFeesAnswer,
  CancelDeclarationAnswer,
  CatalogueAnswer,
  CatalogueCard,
  DeclarePaidAnswer,
  PaymentRequestAnswer,
  QuoteAnswer,
  QuoteWithFeeAnswer,
  SimulationSettingsAnswer,
  VehicleDetailAnswer,
  VerificationRequestedAnswer,
} from "@hakili/contracts";
import { t } from "@hakili/i18n";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import type { ProfileView, ProfileWrite } from "../routes/declared-profile.js";
import type { ProfileBasis } from "../routes/profile-basis.js";
import type { FileFeeView } from "../routes/dossier.js";
import type { FrontQueueEntry } from "../routes/queue-front.js";
import type { ClientOfferView, CodeIssued } from "../routes/offres.js";
import { type VoucherView, voucherFrom } from "../routes/retrait.js";
import { type CashView, cashFrom } from "../routes/comptant.js";

import { memoriserNumero, rememberSecret, numeroMemorise, oublierNumero, handsetSecret } from "./appareil.js";
import {
  type ClientDocuments,
  type DepositLinkView,
  depositByLink,
  depositClientDocument,
  readClientDocuments,
  readDepositLink,
  submitClientDocuments,
} from "./documents.js";
import {
  anonyme,
  avance,
  type EtapeAnonyme,
  entre,
  estNouveauCompte,
  stepAfterVerification,
  identityFrom,
  messageOf,
  refuse,
  type Session,
  START,
} from "./session.js";

/**
 * `P1-CLW-007` and `P1-CLW-008`: the thin part. Every decision lives in
 * `session.ts`; this holds the client, the state and the effects.
 *
 * **The client is built once and kept in a ref**, so the access token it holds
 * survives a re-render and dies with the page. `ApiClient` already carries the
 * refresh dance , `credentials: "include"`, one in-flight refresh, a retry, and
 * `onSessionEnded` when the refresh itself fails , so nothing here
 * re-implements it.
 *
 * **Five calls, and each is one of WF-CLI-01's steps.** They are named for the
 * step rather than for the route, so a reader holding 03 open can follow.
 */

export interface ClientApp {
  readonly session: Session;
  readonly queryClient: QueryClient;
  /** WF-CLI-01, step 2: `Recevoir mon code`. */
  readonly demanderCode: (phone: string) => Promise<void>;
  /** WF-CLI-01, step 3: `Confirmer`. Branches on the ANSWER's shape. */
  readonly confirmerCode: (code: string) => Promise<void>;
  /** WF-CLI-01, step 4: `Continuer`, which is what creates the account. */
  /**
   * `REG-PROFILE-001`: creates the account, then writes the declared profile with the session it issued.
   * If the account exists but the profile was refused, a retry writes the profile only: the enrolment token is spent.
   */
  readonly enregistrer: (fullName: string, city: string, profile: ProfileWrite) => Promise<void>;
  /** `GET /api/v1/me/profile`: the client's own declared profile. */
  readonly loadProfile: () => Promise<ProfileView>;
  /** `PUT /api/v1/me/profile`: the whole declaration, plus the name and city beside it. */
  readonly saveProfile: (
    write: ProfileWrite & { readonly fullName: string; readonly city: string },
  ) => Promise<readonly string[]>;
  /** `GET /api/v1/me/prequalification/basis`: what the eligibility check would read, and why it could not. */
  readonly loadPrequalificationBasis: () => Promise<ProfileBasis>;
  /** `POST /api/v1/prequalifications` WITHOUT the salary: the API reads the declared profile. */
  readonly prequalifyFromProfile: (input: {
    readonly instalmentAmount: number;
    readonly simulationId?: string;
  }) => Promise<unknown>;
  /** WF-CLI-01, step 5: `Créer mon compte`, which binds the PIN to this handset. */
  readonly choisirCode: (pin: string) => Promise<void>;
  /** `S5-1 (a)`: the owner of a known number confirmed that a new PIN replaces the old one. */
  readonly confirmReset: () => void;
  /** `S5-1 (a)`: he did not; the verified session is dropped and the PIN login is offered. */
  readonly abandonReset: () => void;
  /** WF-CLI-02: `Se connecter`. */
  readonly connecter: (pin: string) => Promise<void>;
  /**
   * `P1-DOS-026`'s reads, scoped by the TOKEN.
   *
   * `GET /api/v1/me/dossiers` carries no identifier, and that is `P1-IDN-007`'s
   * client scope rule: *"every `/me` handler resolves the subject from the
   * TOKEN"*. A call that took a dossier id from the app could be pointed at
   * somebody else's file by anybody who could edit a URL.
   */
  readonly chargerDossier: () => Promise<{
    dossier: unknown;
    /** `C7.1`: the buyer's own pieces door; `null` when it did not answer. */
    documents: ClientDocuments | null;
  }>;
  /** `C7.1`: the buyer's pieces alone, re-read after a deposit or a send. */
  readonly loadDocuments: (dossierId: string) => Promise<ClientDocuments | null>;
  /** WF-CLI-18 step 6: « Envoyer » on one named pièce , ticket, PUT, confirm. */
  readonly depositDocument: (
    dossierId: string,
    code: string,
    file: File,
    declaredMonths?: number,
  ) => Promise<void>;
  /** WF-CLI-18 step 7: « Envoyer mon dossier à la vérification ». */
  readonly submitDocuments: (dossierId: string) => Promise<void>;
  /** `C7.3`: the public page's two crossings, by the link's token. */
  readonly readDepositLink: (token: string) => Promise<DepositLinkView>;
  readonly depositByLink: (token: string, file: File, declaredMonths?: number) => Promise<DepositLinkView>;
  /** Moves between the wizard's screens without touching the server. */
  readonly aller: (etape: EtapeAnonyme) => void;
  /** WF-CLI-02 step 1's `Ce n'est pas vous ?`: forget this handset's number. */
  readonly changerDeCompte: () => void;
  readonly deconnecter: () => void;
  /** `P3-CLW-001`: the buyer's own « Une idée ? », through the feature-request door. */
  readonly submitFeatureRequest: (demande: {
    readonly faire: string;
    readonly manque: string;
    readonly permettrait: string;
    readonly contexte: string;
  }) => Promise<void>;
  /** `CW.2`: the search door, its filters passed as the door names them. */
  readonly searchVehicles: (query: Readonly<Record<string, string>>) => Promise<readonly CatalogueCard[]>;
  readonly loadBuyerLists: () => Promise<{
    readonly favourites: readonly string[];
    readonly passed: readonly string[];
  }>;
  readonly keepVehicle: (id: string, kept: boolean) => Promise<void>;
  readonly passVehicle: (id: string, passed: boolean) => Promise<void>;
  readonly clearPassed: () => Promise<void>;
  /** `AVAILABILITY-QUEUE`, `M2`, point 3: the client's own front positions. */
  readonly loadFrontQueue: () => Promise<readonly FrontQueueEntry[]>;
  readonly loadFlags: () => Promise<Readonly<Record<string, boolean>>>;
  readonly loadVehicle: (id: string) => Promise<VehicleDetailAnswer>;
  readonly loadSimulationSettings: () => Promise<SimulationSettingsAnswer>;
  readonly askVisit: (vehicleId: string, origin: "app" | "appel") => Promise<void>;
  readonly prequalify: (input: {
    readonly netSalaryAmount: number;
    readonly existingLoanInstalmentAmount: number;
    readonly instalmentAmount: number;
    readonly employment: string;
    readonly bankName?: string;
  }) => Promise<unknown>;
  /** `round N+3`, `S2-14`: the caller's latest declaration, to pre-fill `WF-CLI-05`'s form. */
  readonly mePrequalification: () => Promise<unknown>;
  readonly loadMakes: () => Promise<readonly string[]>;
  readonly loadCities: () => Promise<readonly string[]>;
  /**
   * `round C3-A`, `C3-A.0(c)`: **`WF-CLI-08`'s door, which this app had never
   * called.**
   *
   * `P1-DOS-026` shipped `POST /api/v1/me/dossiers` in round 192 and the
   * integration tier and the pair-maker have driven it ever since. **The client
   * app never did**: « Ouvrir mon dossier » navigated to a tab and called
   * nothing, so the buyer arrived at a screen that correctly reported he had no
   * file, because he had none , and nothing anywhere errored.
   */
  /**
   * `round C3-A`, `C3-A.2`: the three answers `WF-CLI-10`'s screen renders,
   * read together so the screen composes nothing and holds no copy of the
   * mandate's words.
   */
  /** `L-1`, item 3: the terms are the FILE's , the buyer's own read, which the app already holds; no caller hands a slider's. */
  readonly loadPaiement: (
    dossierId: string,
    file: { readonly depositPct: number | null; readonly durationMonths: number | null },
  ) => Promise<{
    readonly fees: BuyerFeesAnswer;
    readonly mandate: unknown;
    readonly extraInterestAmount: number | null;
    /** `C3-B.0`: the ONE estimate, the fee inside, as the mandate reads it. */
    readonly estimateAmount: number | null;
  }>;
  /**
   * `round C3-B`, `C3-B.0`: **the one estimate, by the door.** Three
   * questions, one figure: the base financed at these terms, the fee
   * `payments` puts on it, then the SAME plan with the fee inside. The screen
   * carries one door's answer into the next door's question and computes
   * nothing. `null` when a door could not answer , the screen prints a dash.
   */
  readonly quoteEstimate: (input: {
    readonly priceAmount: number;
    readonly depositPct?: number;
    readonly durationMonths?: number;
  }) => Promise<Estimate | null>;
  /** `C3-B.2`: the fiche's coverage and control date, from expertise's own days. */
  readonly loadCoverage: (vehicleId: string) => Promise<Coverage | null>;
  /** The amendment (1d): the verification fee for this vehicle, by payments. */
  readonly loadVerificationFeeQuote: (vehicleId: string) => Promise<{
    readonly clientAmount: number | null;
    readonly reducedFlatApplied: boolean;
    /** `PAY-P`: the door's sentence while no price is published; the amount is then `null`. */
    readonly unpublished?: string;
  } | null>;
  readonly acceptMandate: (
    dossierId: string,
    input: { readonly feeFinancingRequested: boolean; readonly mandateVersion: number },
  ) => Promise<void>;
  /** `WF-CLI-10` §7: ask to pay the verification fee, by the chosen means. */
  readonly askVerificationFee: (
    dossierId: string,
    input: { readonly method: string; readonly msisdn?: string },
  ) => Promise<{ readonly reference: string; readonly paymentRequestId: string }>;
  /** #493 addendum 1: cancel the active fee declaration , the way back to the choice of means. */
  readonly cancelFeeDeclaration: (dossierId: string) => Promise<void>;
  /** #493 addendum 2: « J'ai effectué le paiement », the optional reference printed on the receipt. */
  readonly declareFeePaid: (dossierId: string, reference: string | null) => Promise<string>;
  /** §8: where one payment request of this buyer's stands. */
  readonly readPayment: (paymentId: string) => Promise<{ readonly status: string }>;
  /** §9: « Lancer les vérifications » , the one stage a client's hand moves. */
  readonly launchVerification: (dossierId: string) => Promise<void>;
  /** `T-1c`: his file fee by his own door; `null` while no fee is born (the loan not granted) or the file is not his. */
  readonly loadFileFee: (dossierId: string) => Promise<FileFeeView | null>;
  /** `T-2` (WF-CLI-27): the bon de retrait, or the block and its reason; `null` while no settlement stands. */
  readonly loadVoucher: (dossierId: string) => Promise<VoucherView | null>;
  /** `T-2b`, `M2`: the cash settlement , read, opened by the option, declared (WF-CLI-13, WF-CLI-28). */
  readonly loadCash: (dossierId: string) => Promise<CashView | null>;
  readonly openCash: (dossierId: string) => Promise<CashView | null>;
  readonly declareCash: (dossierId: string) => Promise<CashView | null>;
  /** `T-1c`: « J'ai réglé les frais de dossier » , the means used and his reference, `Idempotency-Key` minted here. */
  readonly declareFileFee: (dossierId: string, means: string, reference: string) => Promise<FileFeeView>;
  /** `W-13`: the buyer's abandon door. */
  readonly abandon: (dossierId: string) => Promise<void>;
  /** `PRICE-GAP-DEALER`, M2: « J'accepte ce prix ». */
  readonly acceptPrice: (dossierId: string) => Promise<void>;
  /** Round `C6`, `M4`: « Voir le rapport » , the cabinet's document and Hakili's sheet, the current version. */
  readonly readReport: (dossierId: string) => Promise<ReportReadingView | null>;
  /** Round `PB-2`, `M4`: the handsets the PIN is bound to, and this handset's notices. */
  readonly loadDevices: () => Promise<Handsets | null>;
  /** `P1-IDN-058`: the notices read. */
  readonly readNotices: () => Promise<void>;
  /** `P0-IDN-057`: a handset retired. */
  readonly retireDevice: (deviceId: string) => Promise<void>;
  /** Round `L-2`, `M2`: the buyer's offers, every figure the bank's own. */
  readonly loadOffers: () => Promise<readonly ClientOfferView[]>;
  /** Round `L-2`, `M1`: the code for this act, on this offer. */
  readonly askAcceptanceCode: (offerId: string) => Promise<CodeIssued>;
  /** Round `L-2`, `M1`: the acceptance by the code. */
  readonly acceptOffer: (
    offerId: string,
    code: string,
  ) => Promise<{ circuit: "comite" | "signature" | null }>;
  /** Round `L-2`, `M5` (WF-CLI-23): the buyer's decision on an unfinanced warranty, by his own door. */
  readonly settleWarranty: (dossierId: string, decision: "comptant" | "retiree") => Promise<void>;
  readonly openDossier: (input: {
    readonly vehicleId: string;
    readonly intention: "financement" | "comptant";
    readonly amountFinanced?: number;
    readonly depositPct?: number;
    readonly durationMonths?: number;
  }) => Promise<{ readonly id: string }>;
}

/**
 * `C3-B.0`: what `POST /simulation/quote` answers with the fee inside , **the contract's own fields, picked** (`GENERIC-DOOR-SWEEP`,
 * Arc 2): the door is returned by {@link QuoteAnswer} and an integration spec parses its real answer with the same schema, so
 * this type can no longer drift from what the API sends. `depositAmount` and `vehicleFinancedAmount` are the amendment's: the
 * deposit in francs and the base financed, the door's own.
 */
export type Estimate = Pick<
  QuoteAnswer,
  | "estimateAmount"
  | "instalmentAmount"
  | "insuranceAmount"
  | "amountFinanced"
  | "fileFeeAmount"
  | "depositPct"
  | "durationMonths"
  | "nominalRatePct"
  | "depositAmount"
  | "vehicleFinancedAmount"
>;

/** `C3-B.2`: what `GET /expertise/coverage` answers. */
export interface Coverage {
  readonly city: string | null;
  readonly covered: boolean;
  readonly cabinetName: string | null;
  readonly nextControlDay: { readonly label: string; readonly dayOn: string } | null;
}

/**
 * The handset, as the API's PIN login asks for it , **hashed before it leaves**.
 *
 * WF-CLI-02: *"A device that has never been verified never accepts a PIN and
 * always requires OTP."* The server binds the PIN to a device; this is what the
 * app can honestly say about the one it is running on. It is deliberately NOT a
 * fingerprinting library , this is an identifier the browser already gives and
 * that a person could change by changing browser, which is exactly the event
 * that SHOULD force a re-verification.
 *
 * **`P0-IDN-045`: it is a sha256 hex now, exactly 64 characters.** The raw
 * expression was around 117 and both columns that store it hold 64, so the
 * first login a browser ever completed on staging answered `500`. The digest
 * also means the raw user agent is never stored , see `empreinteAppareil` for
 * the whole reasoning and its one cost.
 */
/**
 * Where an anonymous visitor lands, and it is `Bienvenue` , always.
 *
 * **The plan names the trigger and it is not a page load.** WF-CLI-02 reads
 * *"Trigger: taps `J'ai deja un compte`"*, so the login screen is something a
 * person ASKS for; landing them on it because the handset happens to remember a
 * number would be a route the workflow does not have. What the memory decides
 * is what that tap FINDS , `aller` sends a remembered handset to `Connexion`
 * with its own number masked on it, and an unremembered one to `numero`, which
 * is step 3's OTP path.
 *
 * Deciding this in ONE function is what keeps the boot path, the sign-out path
 * and the refresh-failure path from drifting into three answers to one
 * question.
 */
const depart = (): Session => anonyme("bienvenue");

const empreinte = (): Promise<string> =>
  empreinteAppareil(globalThis.navigator.userAgent, globalThis.navigator.language);

/**
 * **ONE DECISION FOR EVERY REFUSAL, and it is: report by STATE, never by
 * throwing.** `P1-CLW-028` and `P1-CLW-031`, answered together in round 185
 * because answering either alone would have been worse than answering neither.
 *
 * This file had three habits at once. Some paths swallowed (the boot probe),
 * some set a refusing session (`identify`'s `null` branch), and five callbacks
 * did BOTH , they set the state and then `throw failure` on the way out.
 *
 * **The rethrow reached nobody.** Every consumer is a screen calling
 * `void demander(...).finally(...)`, and `void` on a rejected promise is an
 * unhandled rejection: the screen already showed the right sentence, and the
 * browser additionally printed a red the founder saw beside a working refusal on
 * 29 August 2026. A throw nobody catches is not error handling; it is noise with
 * a stack trace.
 *
 * So, stated once and applied everywhere below:
 *
 * 1. **A refusal sets the refusing session and returns.** The screens render
 *    `refusal` already; that is the whole reporting channel.
 * 2. **Nothing here rethrows.** If a caller one day needs to know, it reads the
 *    session , the same fact, from the place that already holds it.
 * 3. **The boot probe is the one deliberate silence, and it is documented as
 *    one.** `POST /auth/refresh` with no cookie answers `401` BY DESIGN: it is
 *    how an anonymous cold start is detected, DevTools colours it red because it
 *    colours every `401`, and it is not a failure. Anonymous is the answer.
 * 4. **`identify` handles its own failure**, so its two outcomes agree: a `null`
 *    identity and an unreachable `/me` both mean *I cannot seat you*, and both
 *    land on the same screen with the same sentence.
 */
/**
 * `round C3-H`: **the launch, as one function a screen's spec can hand its own
 * client to.** The founder's finding of 12 September on staging: the tracking
 * card's « Lancer les vérifications » fired no request , the door existed,
 * the control did not call it. A card's unit spec now mounts the card over
 * THIS function and a recording fetch, so the request's method and path are
 * the witness, not the control's presence.
 */
/** Round `PB-2`, `M4`, `P0-IDN-057`: one handset the client's PIN is bound to, as the devices door answers it. */
export interface HandsetView {
  readonly id: string;
  readonly addedAt: string;
  readonly lastSeenAt: string | null;
  /** Set while this handset's cooling period holds the account's sensitive acts (`P1-IDN-058`). */
  readonly heldUntil: string | null;
  /** The handset asking. */
  readonly current: boolean;
}

/** `P1-IDN-058`: what this handset was told , a handset added, a PIN reset through another one. */
export interface HandsetNotice {
  readonly id: string;
  readonly kind: "deviceAdded" | "pinReset";
  readonly at: string;
  /** The api's own sentence, shown as sent. */
  readonly message: string;
}

export interface Handsets {
  readonly handsets: readonly HandsetView[];
  readonly notices: readonly HandsetNotice[];
}

const isText = (value: unknown): value is string => typeof value === "string" && value !== "";
const isTextOrNull = (value: unknown): value is string | null => value === null || isText(value);

/**
 * `R40`'s third leg: the devices door's answer read field by field, and `null` when it is not the shape
 * the door answers , a row the parser cannot read is never drawn as a handset, and never silently dropped
 * while its neighbours are drawn.
 */
export function handsetsFrom(raw: unknown): Handsets | null {
  const body = raw as { devices?: unknown; notices?: unknown } | null;
  if (body === null || typeof body !== "object") return null;
  if (!Array.isArray(body.devices) || !Array.isArray(body.notices)) return null;
  const handsets: HandsetView[] = [];
  for (const row of body.devices as Record<string, unknown>[]) {
    if (
      !isText(row["id"]) ||
      !isText(row["addedAt"]) ||
      !isTextOrNull(row["lastSeenAt"]) ||
      !isTextOrNull(row["heldUntil"]) ||
      typeof row["current"] !== "boolean"
    )
      return null;
    handsets.push({
      id: row["id"],
      addedAt: row["addedAt"],
      lastSeenAt: row["lastSeenAt"],
      heldUntil: row["heldUntil"],
      current: row["current"],
    });
  }
  const notices: HandsetNotice[] = [];
  for (const row of body.notices as Record<string, unknown>[]) {
    const kind = row["kind"];
    if (!isText(row["id"]) || !isText(row["at"]) || !isText(row["message"])) return null;
    if (kind !== "deviceAdded" && kind !== "pinReset") return null;
    notices.push({ id: row["id"], kind, at: row["at"], message: row["message"] });
  }
  return { handsets, notices };
}

/** `GET /api/v1/auth/client/devices`: the client's handsets, and this handset's unread notices. */
export const loadDevicesBy = async (api: ApiClient): Promise<Handsets | null> =>
  handsetsFrom(await api.request<unknown>("/api/v1/auth/client/devices", "get", {}));

/** `POST /api/v1/auth/client/devices/notices/read`: this handset has read what it was told. */
export const readNoticesBy = async (api: ApiClient): Promise<void> => {
  await api.request<unknown>("/api/v1/auth/client/devices/notices/read", "post");
};

/**
 * `CLIENT-PROFILE-RETURN` (`P0-DOS-075`): `PUT /api/v1/me/profile`, the answer read with the contract's lenient twin (A48: no client reads it untyped). Returns the `notices` the save served, verbatim,
 * `[]` when none; an answer that is not the contract's fails loudly rather than reading as « no notice ».
 */
export const saveProfileBy = async (
  api: ApiClient,
  write: ProfileWrite & { readonly fullName: string; readonly city: string },
): Promise<readonly string[]> =>
  clientProfileWriteAnswerLenientSchema.parse(
    await api.request<unknown>("/api/v1/me/profile", "put", { body: { ...write } }),
  ).notices;

/** `DELETE /api/v1/auth/client/devices/{id}`: a handset retired , refused, in words, while one is cooling. */
export const retireDeviceBy = async (api: ApiClient, deviceId: string): Promise<void> => {
  await api.request<unknown>("/api/v1/auth/client/devices/{id}", "delete", { params: { id: deviceId } });
};

/**
 * Round `L-2`, `M2`: `GET /api/v1/me/offers`, the door's answer read as sent ,
 * `scripts/client-offers-shape.spec.ts` holds the screen's type to the api's.
 */
export const loadOffersBy = async (api: ApiClient): Promise<readonly ClientOfferView[]> => {
  const answer = await api.request<{ offers?: ClientOfferView[] }>("/api/v1/me/offers", "get", {});
  return answer.offers ?? [];
};

/** Round `L-2`, `M1`: the code for this act, on this offer , the door names the channel and the masked number. */
export const askAcceptanceCodeBy = async (api: ApiClient, offerId: string): Promise<CodeIssued> =>
  api.request<CodeIssued>("/api/v1/me/offers/{id}/acceptance-code", "post", { params: { id: offerId } });

/** Round `L-2`, `M1`: the acceptance, by the six digits. */
export const acceptOfferBy = async (
  api: ApiClient,
  offerId: string,
  code: string,
): Promise<{ circuit: "comite" | "signature" | null }> =>
  api.request<{ circuit: "comite" | "signature" | null }>("/api/v1/me/offers/{id}/accept", "post", {
    params: { id: offerId },
    body: { code },
  });

/** Round `L-2`, `M5`: `PUT /api/v1/me/dossiers/{id}/warranty-settlement`, the loan untouched, the fees still due. */
export const settleWarrantyBy = async (
  api: ApiClient,
  dossierId: string,
  decision: "comptant" | "retiree",
): Promise<void> => {
  await api.request<unknown>("/api/v1/me/dossiers/{id}/warranty-settlement", "put", {
    params: { id: dossierId },
    body: { decision },
  });
};

/** `T-1c`: the door's answer, shaped; the screen prints what the door said and nothing it composed. */
export const fileFeeFrom = (raw: unknown): FileFeeView | null => {
  if (typeof raw !== "object" || raw === null) return null;
  const value = (raw as { fileFee?: unknown }).fileFee;
  if (typeof value !== "object" || value === null) return null;
  const fee = value as Record<string, unknown>;
  const hakili = fee["hakili"] as { amount?: unknown } | undefined;
  const bank = fee["bank"] as { amount?: unknown; recipient?: unknown } | undefined;
  const state = fee["state"];
  if (state !== "aRegler" && state !== "declare" && state !== "encaisse" && state !== "financeParLaBanque")
    return null;
  const means = Array.isArray(fee["means"]) ? fee["means"] : [];
  const declaration = fee["declaration"] as Record<string, unknown> | null | undefined;
  return {
    state,
    hakiliAmount: typeof hakili?.amount === "number" ? hakili.amount : 0,
    bankAmount: typeof bank?.amount === "number" ? bank.amount : 0,
    bankName: typeof bank?.recipient === "string" ? bank.recipient : null,
    means: means.flatMap((one: unknown) => {
      const row = one as Record<string, unknown>;
      if (typeof row["code"] !== "string") return [];
      const account = row["account"] as Record<string, unknown> | null | undefined;
      return [
        {
          code: row["code"],
          account:
            account !== null &&
            account !== undefined &&
            typeof account["bankName"] === "string" &&
            typeof account["accountName"] === "string" &&
            typeof account["accountNumber"] === "string"
              ? {
                  bankName: account["bankName"],
                  accountName: account["accountName"],
                  accountNumber: account["accountNumber"],
                }
              : null,
        },
      ];
    }),
    declaration:
      declaration !== null &&
      declaration !== undefined &&
      typeof declaration["means"] === "string" &&
      typeof declaration["reference"] === "string" &&
      typeof declaration["at"] === "string"
        ? { means: declaration["means"], reference: declaration["reference"], at: declaration["at"] }
        : null,
    collectedAt: typeof fee["collectedAt"] === "string" ? fee["collectedAt"] : null,
  };
};

export const loadFileFeeBy = async (api: ApiClient, dossierId: string): Promise<FileFeeView | null> => {
  try {
    return fileFeeFrom(
      await api.request<unknown>("/api/v1/me/dossiers/{id}/file-fee", "get", { params: { id: dossierId } }),
    );
  } catch (error) {
    // No fee born yet (the loan not granted): the door's 404, and the card shows no block.
    if (error instanceof ApiError && error.status === 404) return null;
    throw error;
  }
};

export const loadVoucherBy = async (api: ApiClient, dossierId: string): Promise<VoucherView | null> => {
  try {
    return voucherFrom(
      await api.request<unknown>("/api/v1/me/dossiers/{id}/pickup-voucher", "get", {
        params: { id: dossierId },
      }),
    );
  } catch (error) {
    // No settlement yet (the bank has not ordered): the door's 404, and the screen says so.
    if (error instanceof ApiError && error.status === 404) return null;
    throw error;
  }
};

export const loadCashBy = async (api: ApiClient, dossierId: string): Promise<CashView | null> => {
  try {
    return cashFrom(
      await api.request<unknown>("/api/v1/me/dossiers/{id}/cash-settlement", "get", {
        params: { id: dossierId },
      }),
    );
  } catch (error) {
    if (error instanceof ApiError && error.status === 404) return null;
    throw error;
  }
};

export const openCashBy = async (api: ApiClient, dossierId: string): Promise<CashView | null> =>
  cashFrom(
    await api.request<unknown>("/api/v1/me/dossiers/{id}/cash-settlement", "post", {
      params: { id: dossierId },
    }),
  );

export const declareCashBy = async (api: ApiClient, dossierId: string): Promise<CashView | null> =>
  cashFrom(
    await api.request<unknown>("/api/v1/me/dossiers/{id}/cash-settlement/declare", "post", {
      params: { id: dossierId },
      idempotencyKey: globalThis.crypto.randomUUID(),
    }),
  );

export const declareFileFeeBy = async (
  api: ApiClient,
  dossierId: string,
  means: string,
  reference: string,
): Promise<FileFeeView> => {
  const answer = await api.request<unknown>("/api/v1/me/dossiers/{id}/file-fee/declare", "post", {
    params: { id: dossierId },
    body: { means, reference },
    idempotencyKey: globalThis.crypto.randomUUID(),
  });
  const fee = fileFeeFrom(answer);
  if (fee === null) throw new Error("the fee door answered no fee");
  return fee;
};

export const launchVerificationBy = async (api: ApiClient, dossierId: string): Promise<void> => {
  await api.request<unknown>("/api/v1/me/dossiers/{id}/launch-verification", "post", {
    params: { id: dossierId },
  });
};

/** `PRICE-GAP-DEALER`, M2: « J'accepte ce prix » , the buyer consents to the price the seller proposed; the file restarts. */
export const acceptPriceBy = async (api: ApiClient, dossierId: string): Promise<void> => {
  await api.request<unknown>("/api/v1/me/dossiers/{id}/accept-price", "post", { params: { id: dossierId } });
};

/** #493 addendum 1: the buyer cancels his active fee declaration (self-service, audited, refused once confirmed). */
export const cancelFeeDeclarationBy = async (api: ApiClient, dossierId: string): Promise<void> => {
  await api.request<CancelDeclarationAnswer>("/api/v1/me/dossiers/{id}/fees/verification/cancel", "post", {
    params: { id: dossierId },
    body: {},
  });
};

/** #493 addendum 2: « J'ai effectué le paiement » , the buyer's signal, with his receipt's reference when he has one. */
export const declareFeePaidBy = async (
  api: ApiClient,
  dossierId: string,
  reference: string | null,
): Promise<string> => {
  const answer = await api.request<DeclarePaidAnswer>(
    "/api/v1/me/dossiers/{id}/fees/verification/declare-paid",
    "post",
    {
      params: { id: dossierId },
      body: reference === null || reference.trim() === "" ? {} : { reference: reference.trim() },
    },
  );
  // The door's date, never the browser's clock.
  return answer.declaredAt;
};

/** `round W-13`, `M1`/`M2`: the buyer's abandon door, WF-CLI-16 , the file closed, the hold released, the refund ordered where 02 §2.4.6 says. */
export const abandonBy = async (api: ApiClient, dossierId: string): Promise<void> => {
  await api.request<unknown>("/api/v1/me/dossiers/{id}/abandon", "post", { params: { id: dossierId } });
};

/**
 * Round `C6`, `M4` (`OD-31`, `OD-33`), WF-CLI-14 step 2: **the report of
 * record, read by the buyer.** The door answers the CURRENT version's two
 * documents , the cabinet's own paper and Hakili's derived sheet , each with
 * its fingerprint and a link signed for this answer. The link is a
 * credential: opened by the screen, kept by nothing, printed nowhere.
 */
export interface ReadableDocumentView {
  readonly documentId: string;
  readonly fileName: string;
  readonly contentHash: string;
  readonly url: string;
}
export interface ReportReadingView {
  readonly cabinetName: string;
  readonly version: number;
  readonly current: {
    readonly version: number;
    readonly document: ReadableDocumentView | null;
    readonly sheet: ReadableDocumentView | null;
  };
}
export const readReportBy = async (api: ApiClient, dossierId: string): Promise<ReportReadingView | null> => {
  const answer = await api.request<{ report: ReportReadingView | null }>(
    "/api/v1/me/dossiers/{id}/rapport",
    "get",
    {
      params: { id: dossierId },
    },
  );
  return answer.report;
};

/**
 * `round C5-2`, `M0 (a)` , `W-13`'s ruling (ii): **the buyer's file names its
 * seller's contact.** The file door answers `sellerPhone: null` (the number is
 * `partnerships`' row; the map draws no `dossier → partnerships`, the ring rule
 * refusing the ring it would close). On a file whose verdict asks the buyer to
 * negotiate, the session asks `partnerships`' own door under his token and
 * fills the link; a seller with no recorded number leaves the dash (R81). Any
 * other file asks nothing: the number is read where it is used.
 */
export const withSellerContact = async (api: ApiClient, dossier: unknown): Promise<unknown> => {
  if (typeof dossier !== "object" || dossier === null) return dossier;
  const file = dossier as { verdict?: unknown; sellerOrganisationId?: unknown; sellerPhone?: unknown };
  if (file.verdict !== "ecartprix" || typeof file.sellerOrganisationId !== "string") return dossier;
  const contact = await api.request<{ phone?: unknown }>(
    "/api/v1/me/sellers/{organisationId}/contact",
    "get",
    {
      params: { organisationId: file.sellerOrganisationId },
    },
  );
  return { ...file, sellerPhone: typeof contact.phone === "string" ? contact.phone : null };
};

/**
 * Round `PAY-P`, `M3`: **the DATE of the control, from the coverage door, on
 * the card's fee-received and launched states** , the founder's word on the
 * 48-hour sentence (*« I agree with your recommendation »*): a date the buyer
 * can hold, never a delay. Asked where the fiche asks it (`C3-B.2`), on the
 * file's own car, and only for the two states that print it.
 */
export const withControlCoverage = async (api: ApiClient, dossier: unknown): Promise<unknown> => {
  if (typeof dossier !== "object" || dossier === null) return dossier;
  const file = dossier as { stage?: unknown; geste?: unknown; vehicleId?: unknown };
  const wants = (file.stage === "intake" && file.geste === "launch") || file.stage === "expertise";
  if (!wants || typeof file.vehicleId !== "string") return dossier;
  try {
    // The door's own shape, `{ coverage: … }`, as the fiche reads it (`loadCoverage`).
    const answer = await api.request<{ coverage?: Partial<Coverage> }>("/api/v1/expertise/coverage", "get", {
      query: { vehicleId: file.vehicleId },
    });
    const coverage = answer.coverage;
    if (coverage === undefined || typeof coverage.covered !== "boolean") return dossier;
    return {
      ...file,
      controlCoverage: {
        city: typeof coverage.city === "string" ? coverage.city : null,
        covered: coverage.covered,
        cabinetName: typeof coverage.cabinetName === "string" ? coverage.cabinetName : null,
        nextControlDay: coverage.nextControlDay ?? null,
      },
    };
  } catch {
    // The door not answering is a dash's silence on the card, never a delay.
    return dossier;
  }
};

/**
 * Round `L-1`, the ruling of 16 September, item 3 (`M1`'s last reading):
 * **the mandate's screen reads the FILE.** The quote its line and its fee
 * sentence show is asked with the deposit and the term the buyer's own file
 * carries (`GET /me/dossiers/{id}`, 05 §2.4.4's columns), never with the
 * session's slider; a file born before the columns names none, and the door
 * answers its own defaults. Exported so its reading is pinned.
 */
export const loadPaiementFor = async (
  apiClient: ApiClient,
  dossierId: string,
  /** The FILE's own terms as the buyer's read of his file carries them , the read the app already made (`repos.spec.ts` pins the count: the screen asks no second time). */
  file: { readonly depositPct: number | null; readonly durationMonths: number | null },
): Promise<{
  fees: BuyerFeesAnswer;
  mandate: unknown;
  extraInterestAmount: number | null;
  estimateAmount: number | null;
}> => {
  const terms =
    file.depositPct !== null && file.durationMonths !== null
      ? { depositPct: file.depositPct, durationMonths: file.durationMonths }
      : {};

  const fees = await apiClient.request<BuyerFeesAnswer>("/api/v1/me/dossiers/{id}/fees", "get", {
    params: { id: dossierId },
  });

  const mandate =
    fees.intention === "financement"
      ? await apiClient.request<unknown>("/api/v1/me/dossiers/{id}/mandate", "get", {
          params: { id: dossierId },
        })
      : null;

  /**
   * **The quote door is asked for the extra interest, and handed the fee
   * the OTHER door answered.**
   *
   * `04` §4.2.18 draws no edge `payments -> simulation`, so the two answers
   * meet here , the client carrying a number one door gave it into another
   * door's request, which is a client speaking a contract and not a client
   * deriving a figure. Asked only on the financing path, because a cash
   * buyer has no fee to finance.
   */
  let extraInterestAmount: number | null = null;
  let estimateAmount: number | null = null;
  const hakiliAmount = fees.fileFees?.hakiliAmount;
  // The CAR's price, which the fees door already answered , the quote door
  // derives the financed amount from it with the deposit in force, and a
  // price of zero would quote a plan on nothing.
  const priceAmount = "basePriceAmount" in fees.fee ? fees.fee.basePriceAmount : undefined;
  if (typeof hakiliAmount === "number" && typeof priceAmount === "number") {
    // `C3-B.0`: the buyer's own deposit and term when the session still
    // holds them (the fiche's), the defaults otherwise , the file records
    // its financed amount and not its term, flagged in the addendum.
    const quote = await apiClient.request<QuoteWithFeeAnswer>("/api/v1/simulation/quote", "post", {
      body: { priceAmount, fileFeeAmount: hakiliAmount, ...terms },
    });
    extraInterestAmount = quote.extraInterestAmount;
    estimateAmount = quote.estimateAmount;
  }
  return { fees, mandate, extraInterestAmount, estimateAmount };
};

export function useSession(origin: string, fetchImpl?: typeof globalThis.fetch): ClientApp {
  const [session, setSession] = useState<Session>(START);

  const client = useRef<ApiClient | null>(null);
  client.current ??= new ApiClient({
    origin,
    // `P0-IDN-063`: which surface this application is, so the api names its
    // session's cookie for it and no other application can revive it.
    surface: "client",
    onSessionEnded: () => {
      setSession(anonyme("bienvenue"));
    },
    ...(fetchImpl === undefined ? {} : { fetch: fetchImpl }),
  });
  const api = client.current;

  const queries = useRef<QueryClient | null>(null);
  queries.current ??= createQueryClient({
    onSessionExpired: () => {
      api.setAccessToken(null);
      setSession(anonyme("bienvenue"));
    },
  });
  const queryClient = queries.current;

  const identify = useCallback(async (): Promise<void> => {
    try {
      const identity = identityFrom(await api.request<unknown>("/api/v1/me", "get"));
      setSession(
        identity === null ? refuse(anonyme("bienvenue"), t("client.session.inattendue")) : entre(identity),
      );
    } catch (failure) {
      // Rule 4: an answer it could not read and an answer it could not get are
      // the same fact for a person , *I cannot seat you* , so they land on the
      // same screen. This used to escape into `connecter`'s catch, which meant
      // the two halves of one question were answered in two different files.
      setSession(refuse(anonyme("bienvenue"), messageOf(failure, t("common.error.reseau"))));
    }
  }, [api]);

  /**
   * **On boot, try the cookie.** The access token was deliberately forgotten
   * when the page unloaded; the `SameSite=Strict` refresh cookie was not, and it
   * is same-site with this host by `A41`'s design.
   */
  useEffect(() => {
    const run = { live: true };
    void (async () => {
      try {
        const tokens = await api.request<{ accessToken?: string }>("/api/v1/auth/refresh", "post");
        if (!run.live) return;
        if (typeof tokens.accessToken === "string") {
          api.setAccessToken(tokens.accessToken);
          await identify();
          return;
        }
        setSession(depart());
      } catch {
        // **Rule 3, the one deliberate silence.** No cookie, or a spent one:
        // anonymous is the answer, not an error. The `401` DevTools paints red
        // at every cold start is this line working , the probe exists precisely
        // to ask a question whose answer is often no.
        if (run.live) setSession(depart());
      }
    })();
    return () => {
      run.live = false;
    };
  }, [api, identify]);

  const demanderCode = useCallback(
    async (phone: string): Promise<void> => {
      try {
        const answer = await api.request<{ channel?: string }>("/api/v1/auth/client/otp", "post", {
          body: { phone },
        });
        setSession((current) => avance(current, "code", { phone, canal: answer.channel ?? "whatsapp" }));
      } catch (failure) {
        // Rule 2: the state IS the report. Every caller is `void x().finally()`,
        // so a rethrow here only ever produced an unhandled rejection beside a
        // screen that was already saying the right thing.
        setSession((current) => refuse(current, messageOf(failure, t("common.error.reseau"))));
      }
    },
    [api],
  );

  const confirmerCode = useCallback(
    async (code: string): Promise<void> => {
      const phone = session.status === "anonymous" ? session.phone : "";
      try {
        // `P0-IDN-057`: the handset presents the secret it holds, if any; a known number verified on a
        // handset without one is issued one, which is kept before anything else happens.
        const deviceSecret = handsetSecret();
        const answer = await api.request<unknown>("/api/v1/auth/client/otp/verify", "post", {
          body: {
            phone,
            code,
            deviceFingerprint: await empreinte(),
            ...(deviceSecret === undefined ? {} : { deviceSecret }),
          },
        });
        rememberSecret((answer as { deviceSecret?: unknown }).deviceSecret);
        // WF-CLI-01, step 3: a number the server has never seen comes back with
        // an enrolment token and goes to `profil`; a known one comes back
        // signed in and goes to `choixcode` to re-bind this handset.
        if (estNouveauCompte(answer)) {
          setSession((current) => avance(current, "profil", { enrolmentToken: answer.enrolmentToken }));
          return;
        }
        const tokens = answer as { accessToken?: string };
        if (typeof tokens.accessToken !== "string") {
          setSession((current) => refuse(current, t("client.session.inattendue")));
          return;
        }
        api.setAccessToken(tokens.accessToken);
        setSession((current) => avance(current, stepAfterVerification(answer)));
      } catch (failure) {
        // Rule 2: the state IS the report. Every caller is `void x().finally()`,
        // so a rethrow here only ever produced an unhandled rejection beside a
        // screen that was already saying the right thing.
        setSession((current) => refuse(current, messageOf(failure, t("common.error.reseau"))));
      }
    },
    [api, session],
  );

  const accountCreated = useRef(false);

  const enregistrer = useCallback(
    async (fullName: string, city: string, profile: ProfileWrite): Promise<void> => {
      const enrolmentToken = session.status === "anonymous" ? session.enrolmentToken : null;
      try {
        if (accountCreated.current) {
          await api.request<unknown>("/api/v1/me/profile", "put", { body: { ...profile } });
          accountCreated.current = false;
          setSession((current) => avance(current, "choixcode"));
          return;
        }
        const tokens = await api.request<{ accessToken?: string; deviceSecret?: string }>(
          "/api/v1/auth/client/register",
          "post",
          { body: { enrolmentToken, fullName, city, deviceFingerprint: await empreinte() } },
        );
        rememberSecret(tokens.deviceSecret);
        if (typeof tokens.accessToken !== "string") {
          setSession((current) => refuse(current, t("client.session.inattendue")));
          return;
        }
        api.setAccessToken(tokens.accessToken);
        accountCreated.current = true;
        // The profile is written with the session the creation just issued. A refusal stays on this screen
        // (the catch below) and the retry skips the creation, so the client never re-types what he declared.
        await api.request<unknown>("/api/v1/me/profile", "put", { body: { ...profile } });
        accountCreated.current = false;
        setSession((current) => avance(current, "choixcode"));
      } catch (failure) {
        // Rule 2: the state IS the report. Every caller is `void x().finally()`,
        // so a rethrow here only ever produced an unhandled rejection beside a
        // screen that was already saying the right thing.
        setSession((current) => refuse(current, messageOf(failure, t("common.error.reseau"))));
      }
    },
    [api, session],
  );

  const choisirCode = useCallback(
    async (pin: string): Promise<void> => {
      try {
        // The subject is the PRINCIPAL, never the body: the token from step 3
        // or step 4 is already on the client, and the route reads it. That is
        // the API's own asymmetry and this app honours it by sending only the
        // secret.
        await api.request<unknown>("/api/v1/auth/client/pin", "post", { body: { pin } });
        // **This is the moment the handset learns whose it is.** WF-CLI-01 step
        // 5 binds the code to this device; remembering the number is the half
        // that lets WF-CLI-02 mask it and send it next time.
        if (session.status === "anonymous") memoriserNumero(session.phone);
        await identify();
      } catch (failure) {
        // Rule 2: the state IS the report. Every caller is `void x().finally()`,
        // so a rethrow here only ever produced an unhandled rejection beside a
        // screen that was already saying the right thing.
        setSession((current) => refuse(current, messageOf(failure, t("common.error.reseau"))));
      }
    },
    [api, identify, session],
  );

  const confirmReset = useCallback((): void => {
    setSession((current) => avance(current, "choixcode"));
  }, []);

  const abandonReset = useCallback((): void => {
    // The code was verified and a session issued; walking away from the reset leaves neither standing.
    api.setAccessToken(null);
    setSession((current) => avance(current, "connexion"));
  }, [api]);

  const connecter = useCallback(
    async (pin: string): Promise<void> => {
      const phone = session.status === "anonymous" ? session.phone : "";
      try {
        const tokens = await api.request<{ accessToken?: string }>("/api/v1/auth/client/pin/login", "post", {
          body: { phone, pin, deviceFingerprint: await empreinte(), deviceSecret: handsetSecret() },
        });
        if (typeof tokens.accessToken !== "string") {
          setSession((current) => refuse(current, t("client.session.inattendue")));
          return;
        }
        api.setAccessToken(tokens.accessToken);
        memoriserNumero(phone);
        await identify();
      } catch (failure) {
        // Rule 2: the state IS the report. Every caller is `void x().finally()`,
        // so a rethrow here only ever produced an unhandled rejection beside a
        // screen that was already saying the right thing.
        setSession((current) => refuse(current, messageOf(failure, t("common.error.reseau"))));
      }
    },
    [api, identify, session],
  );

  const loadDocuments = useCallback(
    async (dossierId: string): Promise<ClientDocuments | null> => {
      try {
        return await readClientDocuments(api, dossierId);
      } catch {
        // The screen says the list was not answered; it never prints « 0 ».
        return null;
      }
    },
    [api],
  );

  const chargerDossier = useCallback(async (): Promise<{
    dossier: unknown;
    documents: ClientDocuments | null;
  }> => {
    const liste = await api.request<{ dossiers?: unknown[] }>("/api/v1/me/dossiers", "get");
    // `P1-DOS-034`'s one-live-file rule is what makes "the first row" the right
    // row: a buyer has at most one open file, so a list of one is the answer and
    // a chooser would be a screen for a choice that cannot arise.
    const premier = (liste.dossiers ?? [])[0] ?? null;
    if (premier === null) return { dossier: null, documents: null };
    const id = (premier as { id?: unknown }).id;
    if (typeof id !== "string") return { dossier: premier, documents: null };
    const [detail, documents] = await Promise.all([
      api.request<{ dossier?: unknown }>("/api/v1/me/dossiers/{id}", "get", { params: { id } }),
      loadDocuments(id),
    ]);
    return {
      dossier: await withControlCoverage(api, await withSellerContact(api, detail.dossier ?? premier)),
      documents,
    };
  }, [api, loadDocuments]);

  // The browser's own `fetch` for the presigned PUT , the api client never
  // sees the store's URL, and the spec's recording fetch sees it instead.
  const uploads = useRef(fetchImpl ?? globalThis.fetch.bind(globalThis));
  const depositDocument = useCallback(
    async (dossierId: string, code: string, file: File, declaredMonths?: number): Promise<void> => {
      await depositClientDocument(api, uploads.current, dossierId, code, file, declaredMonths);
    },
    [api],
  );
  const submitDocuments = useCallback(
    async (dossierId: string): Promise<void> => {
      await submitClientDocuments(api, dossierId);
    },
    [api],
  );
  const readDepositLinkBy = useCallback(
    async (token: string): Promise<DepositLinkView> => readDepositLink(api, token),
    [api],
  );
  const depositByLinkBy = useCallback(
    async (token: string, file: File, declaredMonths?: number): Promise<DepositLinkView> =>
      depositByLink(api, uploads.current, token, file, declaredMonths),
    [api],
  );

  /**
   * WF-CLI-02 step 3's own routing, applied in both directions.
   *
   * `J'ai déjà un compte` on a handset that remembers nothing must go to the
   * NUMBER, not to a PIN field with no account behind it , the workflow's actor
   * is a *"registered client on a recognised device"*, and an unrecognised one
   * takes the OTP path. Without this, the fix for the empty phone would simply
   * become a stored empty string and the defect would return wearing new
   * clothes.
   */
  const aller = useCallback((etape: EtapeAnonyme): void => {
    const memorise = numeroMemorise();
    const cible: EtapeAnonyme = etape === "connexion" && memorise === null ? "numero" : etape;
    setSession((current) =>
      current.status === "anonymous"
        ? avance(current, cible, cible === "connexion" && memorise !== null ? { phone: memorise } : {})
        : anonyme(cible, memorise ?? ""),
    );
  }, []);

  /** WF-CLI-02 step 1's `Ce n'est pas vous ?`: the ONE gesture that forgets. */
  const changerDeCompte = useCallback((): void => {
    oublierNumero();
    setSession(anonyme("numero"));
  }, []);

  /**
   * `round CW`, `CW.1`, `P3-CLW-001`: « Une idée ? » on the buyer's surface.
   *
   * The provider's `envoyer` is OPTIONAL, and an app that leaves it out gets a
   * modal answering « Demande transmise » with nothing transmitted , the exact
   * lie `R81` forbids and `P1-UIK-025` was amended over. So it is wired here,
   * to the door `CW.1` opened to a `client` principal.
   *
   * It THROWS on refusal rather than returning a result, because that is the
   * contract the provider states: it awaits `envoyer` and only then says the
   * demand was transmitted.
   */
  const submitFeatureRequest = useCallback(
    async (demande: {
      readonly faire: string;
      readonly manque: string;
      readonly permettrait: string;
      readonly contexte: string;
    }): Promise<void> => {
      await api.request<unknown>("/api/v1/feature-requests", "post", {
        body: {
          goal: demande.faire,
          missing: demande.manque,
          benefit: demande.permettrait,
          screenLabel: demande.contexte,
        },
      });
    },
    [api],
  );

  /**
   * `round CW`, `CW.2`: the search, its two lists, and the flags.
   *
   * Every one of these is a DOOR and never a guess. The counts the segmented
   * row shows are the lengths of what these answer , `R81` forbids a number
   * nobody computed , and « Découvrir » is on only where the api says so.
   */
  const searchVehicles = useCallback(
    async (query: Readonly<Record<string, string>>): Promise<readonly CatalogueCard[]> => {
      const answer = await api.request<CatalogueAnswer>("/api/v1/vehicles", "get", { query });
      return answer.data;
    },
    [api],
  );

  const loadBuyerLists = useCallback(async (): Promise<{
    readonly favourites: readonly string[];
    readonly passed: readonly string[];
  }> => {
    const [kept, passed] = await Promise.all([
      api.request<{ data?: string[] }>("/api/v1/me/favourites", "get"),
      api.request<{ data?: string[] }>("/api/v1/me/passed-vehicles", "get"),
    ]);
    return { favourites: kept.data ?? [], passed: passed.data ?? [] };
  }, [api]);

  const keepVehicle = useCallback(
    async (id: string, kept: boolean): Promise<void> => {
      await api.request<unknown>("/api/v1/me/favourites/{vehicleId}", kept ? "put" : "delete", {
        params: { vehicleId: id },
      });
    },
    [api],
  );

  const passVehicle = useCallback(
    async (id: string, passed: boolean): Promise<void> => {
      await api.request<unknown>("/api/v1/me/passed-vehicles/{vehicleId}", passed ? "put" : "delete", {
        params: { vehicleId: id },
      });
    },
    [api],
  );

  /** « Revoir les véhicules passés » , the whole list, in one call. */
  const clearPassed = useCallback(async (): Promise<void> => {
    await api.request<unknown>("/api/v1/me/passed-vehicles", "delete");
  }, [api]);

  /**
   * `AVAILABILITY-QUEUE`, `M2`, point 3: « Véhicules disponibles pour vous » ,
   * the client's own front positions, a door and never a guess. The server
   * already excludes a lapsed one (`CLIENT_FRONT_ENTRIES_SQL`'s own guard), so
   * what this answers is exactly what the screen should show, with nothing
   * filtered again here.
   */
  const loadFrontQueue = useCallback(async (): Promise<readonly FrontQueueEntry[]> => {
    const answer = await api.request<{ data?: FrontQueueEntry[] }>("/api/v1/me/queue-front", "get");
    return answer.data ?? [];
  }, [api]);

  /** The makes the dealer's own form reads , one reference for both surfaces. */
  /** One car, with its photographs' read urls derived by `documents`. */
  const loadVehicle = useCallback(
    async (id: string): Promise<VehicleDetailAnswer> =>
      api.request<VehicleDetailAnswer>("/api/v1/vehicles/{id}", "get", { params: { id } }),
    [api],
  );

  /**
   * The constants the simulator reads, and never constants of the bundle's own.
   *
   * `P1-CLW-003`: the screen's instant reading uses the mirror in
   * `@hakili/domain-types` with THESE, and the api's own quote is the
   * authority the two are pinned to.
   */
  const loadSimulationSettings = useCallback(
    async (): Promise<SimulationSettingsAnswer> =>
      api.request<SimulationSettingsAnswer>("/api/v1/simulation/config", "get"),
    [api],
  );

  /** `P1-CAT-010`: « Demander une visite », and the `tel:` link records the same fact. */
  const askVisit = useCallback(
    async (vehicleId: string, origin: "app" | "appel"): Promise<void> => {
      await api.request<unknown>("/api/v1/vehicles/{id}/visits", "post", {
        params: { id: vehicleId },
        body: { origin },
      });
    },
    [api],
  );

  /**
   * `CW.4`, `WF-CLI-05`: the prequalification.
   *
   * Every figure the screen shows , capacity, effort, band, shortfall, the
   * horizon , is in this answer. The screen computes none of them.
   */
  const prequalify = useCallback(
    async (input: {
      readonly netSalaryAmount: number;
      readonly existingLoanInstalmentAmount: number;
      readonly instalmentAmount: number;
      readonly employment: string;
      /** `C2.1`: asked by `WF-CLI-05` step 1, and never read by the door. */
      readonly bankName?: string;
    }): Promise<unknown> => api.request<unknown>("/api/v1/prequalifications", "post", { body: input }),
    [api],
  );

  const loadProfile = useCallback(async (): Promise<ProfileView> => {
    const answer = await api.request<{ data: ProfileView }>("/api/v1/me/profile", "get");
    return answer.data;
  }, [api]);

  const saveProfile = useCallback(
    (
      write: ProfileWrite & { readonly fullName: string; readonly city: string },
    ): Promise<readonly string[]> => saveProfileBy(api, write),
    [api],
  );

  const loadPrequalificationBasis = useCallback(
    async (): Promise<ProfileBasis> =>
      api.request<ProfileBasis>("/api/v1/me/prequalification/basis", "get", {}),
    [api],
  );

  const prequalifyFromProfile = useCallback(
    async (input: { readonly instalmentAmount: number; readonly simulationId?: string }): Promise<unknown> =>
      api.request<unknown>("/api/v1/prequalifications", "post", { body: input }),
    [api],
  );

  /**
   * `round N+3`, `S2-14`: the caller's own latest declaration and verdict,
   * read once so `WF-CLI-05`'s form can open already answered rather than
   * blank on a client who has already declared a salary.
   */
  const mePrequalification = useCallback(
    async (): Promise<unknown> => api.request<unknown>("/api/v1/me/prequalification", "get", {}),
    [api],
  );

  /**
   * `round C3-A`, `C3-A.0(c)`: the buyer opens his own file, `WF-CLI-08`.
   *
   * It returns the id rather than swallowing it, because the caller has to
   * carry the buyer somewhere afterwards and `WF-CLI-05` §6 says where: « Suivi »
   * with the tracking card, never the Dossier tab.
   *
   * **A refusal is thrown, not returned as a falsy value.** `4.5.5` gives the
   * door its own French sentence, and the screen's job is to show it: a gate
   * may close, never in silence, and the silence is what the founder met.
   */
  const openDossier = useCallback(
    async (input: {
      readonly vehicleId: string;
      readonly intention: "financement" | "comptant";
      readonly amountFinanced?: number;
      /** `L-1`, `M1`: the fiche's deposit and term at engagement. */
      readonly depositPct?: number;
      readonly durationMonths?: number;
      /** `OD-37` R7: where the deposit is, as the buyer declared it at the opening step. */
      readonly apportOrigine?: string;
      readonly apportOrigineDetail?: string;
      readonly depositOnSalaryAccount?: boolean;
    }): Promise<{ readonly id: string }> => {
      const answer = await api.request<{ dossier?: { id?: unknown } }>("/api/v1/me/dossiers", "post", {
        body: input,
      });
      const id = answer.dossier?.id;
      if (typeof id !== "string") throw new Error("the door opened no file");
      return { id };
    },
    [api],
  );

  const quoteEstimate = useCallback(
    async (input: {
      readonly priceAmount: number;
      readonly depositPct?: number;
      readonly durationMonths?: number;
    }): Promise<Estimate | null> => {
      const terms = {
        ...(input.depositPct === undefined ? {} : { depositPct: input.depositPct }),
        ...(input.durationMonths === undefined ? {} : { durationMonths: input.durationMonths }),
      };
      const base = await api.request<QuoteAnswer>("/api/v1/simulation/quote", "post", {
        body: { priceAmount: input.priceAmount, ...terms },
      });
      const fee = await api.request<{ hakiliAmount?: number }>("/api/v1/payments/file-fees/quote", "get", {
        query: { financedAmount: String(base.amountFinanced) },
      });
      if (typeof fee.hakiliAmount !== "number") return null;
      const quote = await api.request<QuoteWithFeeAnswer>("/api/v1/simulation/quote", "post", {
        body: { priceAmount: input.priceAmount, ...terms, fileFeeAmount: fee.hakiliAmount },
      });
      return {
        estimateAmount: quote.estimateAmount,
        instalmentAmount: quote.instalmentAmount,
        insuranceAmount: quote.insuranceAmount,
        amountFinanced: quote.amountFinanced,
        fileFeeAmount: quote.fileFeeAmount,
        depositPct: quote.depositPct,
        durationMonths: quote.durationMonths,
        nominalRatePct: quote.nominalRatePct,
        depositAmount: quote.depositAmount,
        vehicleFinancedAmount: quote.vehicleFinancedAmount,
      };
    },
    [api],
  );

  /** The amendment (1d): the verification fee for THIS vehicle, by payments' door. */
  const loadVerificationFeeQuote = useCallback(
    async (
      vehicleId: string,
    ): Promise<{ clientAmount: number | null; reducedFlatApplied: boolean; unpublished?: string } | null> => {
      const answer = await api.request<{
        clientAmount?: number | null;
        reducedFlatApplied?: boolean;
        unpublished?: string;
      }>("/api/v1/payments/verification-fees/quote", "get", { query: { vehicleId } });
      // `PAY-P`: the door answers NO figure and its sentence while no price is published.
      if (typeof answer.unpublished === "string") {
        return { clientAmount: null, reducedFlatApplied: false, unpublished: answer.unpublished };
      }
      if (typeof answer.clientAmount !== "number") return null;
      return { clientAmount: answer.clientAmount, reducedFlatApplied: answer.reducedFlatApplied === true };
    },
    [api],
  );

  const loadCoverage = useCallback(
    async (vehicleId: string): Promise<Coverage | null> => {
      const answer = await api.request<{ coverage?: Partial<Coverage> }>(
        "/api/v1/expertise/coverage",
        "get",
        {
          query: { vehicleId },
        },
      );
      const found = answer.coverage;
      if (found === undefined || typeof found.covered !== "boolean") return null;
      return {
        city: typeof found.city === "string" ? found.city : null,
        covered: found.covered,
        cabinetName: typeof found.cabinetName === "string" ? found.cabinetName : null,
        nextControlDay:
          typeof found.nextControlDay?.label === "string" && typeof found.nextControlDay.dayOn === "string"
            ? { label: found.nextControlDay.label, dayOn: found.nextControlDay.dayOn }
            : null,
      };
    },
    [api],
  );

  const loadPaiement = useCallback(
    (
      dossierId: string,
      file: { readonly depositPct: number | null; readonly durationMonths: number | null },
    ) => loadPaiementFor(api, dossierId, file),
    [api],
  );

  const acceptMandate = useCallback(
    async (
      dossierId: string,
      input: {
        readonly feeFinancingRequested: boolean;
        readonly mandateVersion: number;
        /** R-G: the served estimate, echoed; absent when the door served none. */
        readonly estimatedInstalmentAmount?: number;
      },
    ): Promise<void> => {
      await api.request<unknown>("/api/v1/me/dossiers/{id}/mandate", "post", {
        params: { id: dossierId },
        body: input,
      });
    },
    [api],
  );

  /**
   * `round C3-A`, `C3-A.3`: **`WF-CLI-10` §7 , the buyer asks to pay.**
   *
   * ## The key is a FRESH uuid per attempt , the architect's correction
   *
   * The first cut derived it from the file and the means, so a double tap
   * replayed instead of minting a second reference. **That was the wrong place
   * to solve it, and it broke the retry**: the key IS the reference, a stored
   * answer is served from the row for as long as the row lives, so a buyer
   * whose push failed and who pressed « Recevoir la demande de paiement »
   * again with the same means met his own failure and could never try again.
   *
   * `04` §4.5.7 says a client-generated `uuid` v4, and `C3.5` ruled the same
   * for the corridor. The two concerns it was confusing are separated:
   *
   * - **a bounced thumb is the SCREEN's**: the control is shut while its
   *   request is in flight, which is `busy` and was already true;
   * - **a second demand while one is pending is the DOOR's**: it answers the
   *   pending request and re-sends it on the same reference, or expires it when
   *   the buyer has changed means. The string he quotes on a transfer stays one
   *   because the door keeps it one , never because two taps shared a key.
   */
  const askVerificationFee = useCallback(
    async (
      dossierId: string,
      input: { readonly method: string; readonly msisdn?: string },
    ): Promise<{ reference: string; paymentRequestId: string }> => {
      const answer = await api.request<VerificationRequestedAnswer>(
        "/api/v1/me/dossiers/{id}/fees/verification",
        "post",
        {
          params: { id: dossierId },
          body: input,
          idempotencyKey: globalThis.crypto.randomUUID(),
        },
      );
      return { reference: answer.reference, paymentRequestId: answer.paymentRequestId };
    },
    [api],
  );

  /** §8's own question, asked again while a push is pending. */
  const readPayment = useCallback(
    async (paymentId: string): Promise<{ status: string }> => {
      const answer = await api.request<PaymentRequestAnswer>("/api/v1/me/payments/{id}", "get", {
        params: { id: paymentId },
      });
      return { status: answer.payment.status };
    },
    [api],
  );

  /**
   * `WF-CLI-10` §9. **No `Idempotency-Key`, and that is the door's own rule**:
   * the from-state is the key, and a second press finds the file already gone
   * to `expertise` and is refused in the machine's words , which tells the
   * buyer his gesture landed, where a replayed `200` would not.
   */
  const launchVerification = useCallback(
    (dossierId: string): Promise<void> => launchVerificationBy(api, dossierId),
    [api],
  );
  const cancelFeeDeclaration = useCallback(
    (dossierId: string): Promise<void> => cancelFeeDeclarationBy(api, dossierId),
    [api],
  );
  const declareFeePaid = useCallback(
    (dossierId: string, reference: string | null): Promise<string> =>
      declareFeePaidBy(api, dossierId, reference),
    [api],
  );
  const abandon = useCallback((dossierId: string): Promise<void> => abandonBy(api, dossierId), [api]);
  const acceptPrice = useCallback((dossierId: string): Promise<void> => acceptPriceBy(api, dossierId), [api]);
  const readReport = useCallback(
    (dossierId: string): Promise<ReportReadingView | null> => readReportBy(api, dossierId),
    [api],
  );
  const loadDevices = useCallback((): Promise<Handsets | null> => loadDevicesBy(api), [api]);
  const readNotices = useCallback((): Promise<void> => readNoticesBy(api), [api]);
  const retireDevice = useCallback((deviceId: string): Promise<void> => retireDeviceBy(api, deviceId), [api]);
  const loadOffers = useCallback((): Promise<readonly ClientOfferView[]> => loadOffersBy(api), [api]);
  const askAcceptanceCode = useCallback(
    (offerId: string): Promise<CodeIssued> => askAcceptanceCodeBy(api, offerId),
    [api],
  );
  const acceptOffer = useCallback(
    (offerId: string, code: string): Promise<{ circuit: "comite" | "signature" | null }> =>
      acceptOfferBy(api, offerId, code),
    [api],
  );
  const settleWarranty = useCallback(
    (dossierId: string, decision: "comptant" | "retiree"): Promise<void> =>
      settleWarrantyBy(api, dossierId, decision),
    [api],
  );
  const loadFileFee = useCallback((dossierId: string) => loadFileFeeBy(api, dossierId), [api]);
  const loadVoucher = useCallback((dossierId: string) => loadVoucherBy(api, dossierId), [api]);
  const loadCash = useCallback((dossierId: string) => loadCashBy(api, dossierId), [api]);
  const openCash = useCallback((dossierId: string) => openCashBy(api, dossierId), [api]);
  const declareCash = useCallback((dossierId: string) => declareCashBy(api, dossierId), [api]);
  const declareFileFee = useCallback(
    (dossierId: string, means: string, reference: string) =>
      declareFileFeeBy(api, dossierId, means, reference),
    [api],
  );

  const loadMakes = useCallback(async (): Promise<readonly string[]> => {
    const answer = await api.request<{ data?: { name?: unknown }[] }>(
      "/api/v1/reference/vehicle-makes",
      "get",
    );
    return (answer.data ?? [])
      .map((row) => row.name)
      .filter((name): name is string => typeof name === "string");
  }, [api]);

  const loadCities = useCallback(async (): Promise<readonly string[]> => {
    const answer = await api.request<{ data?: { name?: unknown }[] }>("/api/v1/cities", "get");
    return (answer.data ?? [])
      .map((row) => row.name)
      .filter((name): name is string => typeof name === "string");
  }, [api]);

  const loadFlags = useCallback(async (): Promise<Readonly<Record<string, boolean>>> => {
    const answer = await api.request<{ data?: Record<string, boolean> }>("/api/v1/me/flags", "get");
    return answer.data ?? {};
  }, [api]);

  const deconnecter = useCallback((): void => {
    // `unknown` and not `void`: `void` as a type ARGUMENT is meaningless , the
    // call returns a body this app ignores, which is what `unknown` says.
    void api.request<unknown>("/api/v1/auth/logout", "post").catch(() => undefined);
    api.setAccessToken(null);
    // The number is KEPT: WF-CLI-02 is a person returning to their own handset
    // and typing four digits, and forgetting here would send every returning
    // buyer through an OTP they do not need.
    setSession(depart());
  }, [api]);

  /**
   * **Memoized, and that is the other half of round 185's repair.**
   *
   * This returned a bare object literal until 29 August 2026, so every render
   * handed consumers a new identity. `App.tsx` listed it in an effect's
   * dependency array , against its own comment's warning , and the buyer's file
   * was refetched forever. Fixing that array stops today's loop; **this stops
   * tomorrow's**, because the next consumer to depend on `app` will find a
   * value that only changes when something in it actually changed.
   *
   * `console-web`'s `useSession` has memoized its return from the start. The
   * asymmetry was the whole defect, and it is gone.
   */
  return useMemo(
    () => ({
      session,
      queryClient,
      demanderCode,
      confirmerCode,
      enregistrer,
      choisirCode,
      confirmReset,
      abandonReset,
      connecter,
      changerDeCompte,
      chargerDossier,
      aller,
      deconnecter,
      submitFeatureRequest,
      searchVehicles,
      loadBuyerLists,
      keepVehicle,
      passVehicle,
      clearPassed,
      loadFrontQueue,
      loadFlags,
      loadVehicle,
      loadSimulationSettings,
      askVisit,
      prequalify,
      mePrequalification,
      loadProfile,
      saveProfile,
      loadPrequalificationBasis,
      prequalifyFromProfile,
      openDossier,
      loadPaiement,
      quoteEstimate,
      loadCoverage,
      loadVerificationFeeQuote,
      acceptMandate,
      askVerificationFee,
      readPayment,
      cancelFeeDeclaration,
      declareFeePaid,
      launchVerification,
      abandon,
      acceptPrice,
      readReport,
      loadDevices,
      readNotices,
      retireDevice,
      loadOffers,
      askAcceptanceCode,
      acceptOffer,
      settleWarranty,
      loadFileFee,
      loadVoucher,
      loadCash,
      openCash,
      declareCash,
      declareFileFee,
      loadMakes,
      loadCities,
      loadDocuments,
      depositDocument,
      submitDocuments,
      readDepositLink: readDepositLinkBy,
      depositByLink: depositByLinkBy,
    }),
    [
      session,
      queryClient,
      demanderCode,
      confirmerCode,
      enregistrer,
      choisirCode,
      confirmReset,
      abandonReset,
      connecter,
      changerDeCompte,
      chargerDossier,
      aller,
      deconnecter,
      submitFeatureRequest,
      searchVehicles,
      loadBuyerLists,
      keepVehicle,
      passVehicle,
      clearPassed,
      loadFlags,
      loadMakes,
      loadCities,
      loadDocuments,
      depositDocument,
      submitDocuments,
      readDepositLinkBy,
      depositByLinkBy,
      loadDevices,
      readNotices,
      retireDevice,
      loadOffers,
      askAcceptanceCode,
      acceptOffer,
      settleWarranty,
      loadFileFee,
      loadVoucher,
      loadCash,
      openCash,
      declareCash,
      declareFileFee,
    ],
  );
}
